export type FirebaseTokenInfo = {
  fcmToken: string;
  platform: string;
  deviceId: string;
  appVersion: string;
};

declare global {
  interface Window {
    nativeDevice: {
      getFirebaseToken: () => Promise<FirebaseTokenInfo>;
      getPendingNotifications?: () => Promise<Array<import("@/utils/native-notification").NativeNotificationPayload>>;
      getOsLanguage?: () => Promise<import("@/utils/os-language").OsLanguageInfo | null>;
      getSignature?: (params: { method: string; path: string }) => Promise<import("@/utils/app-signature").AppSignature>;
    };
    flutter_inappwebview?: {
      callHandler: (handlerName: string, ...args: unknown[]) => Promise<unknown>;
    };
  }

  interface WindowEventMap {
    nativeFirebaseTokenChanged: CustomEvent<FirebaseTokenInfo>;
  }
}

const STORAGE_KEY = "last_registered_firebase_token";

export function getLastRegisteredToken(): FirebaseTokenInfo | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function saveLastRegisteredToken(info: FirebaseTokenInfo): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(info));
  } catch {
    // Ignore storage errors
  }
}

export function clearLastRegisteredToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

export async function getFirebaseToken(): Promise<FirebaseTokenInfo | null> {
  if (!window.nativeDevice?.getFirebaseToken) return null;

  try {
    const info = await window.nativeDevice.getFirebaseToken();
    return info || null;
  } catch {
    return null;
  }
}

let listenerInitialized = false;

export function initFirebaseTokenListener(onTokenChanged?: (newInfo: FirebaseTokenInfo) => void): () => void {
  if (listenerInitialized) return () => {};
  listenerInitialized = true;

  const handleEvent = (event: CustomEvent<FirebaseTokenInfo>) => {
    const info = event.detail;
    if (info?.fcmToken) {
      onTokenChanged?.(info);
    }
  };

  window.addEventListener("nativeFirebaseTokenChanged", handleEvent);

  return () => {
    listenerInitialized = false;
    window.removeEventListener("nativeFirebaseTokenChanged", handleEvent);
  };
}
